import {
  ShieldExclamationIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import type { SectionId } from '@/config/features';
import {
  SECURITY_DASHBOARD_PATH,
  SECURITY_ANALYTICS_PATH,
} from '@/config/relocatedRoutes';

/**
 * Indicator source keys map a module to the best-effort headline-count loader in
 * `home.service.ts`. Kept as a string union (not a function) so the config stays
 * serialisable/testable and the page owns the data fetching.
 */
export type HomeIndicatorSource = 'security' | 'legal';

export interface HomeModule {
  /** Stable id, also used as the React key and test hook. */
  id: string;
  /** Feature-flag section this module belongs to (gates visibility per build). */
  sectionId: SectionId;
  /** Module id matched against `user.moduleAccess` via `checkModuleAccess`. */
  moduleId: string;
  /** Card title. */
  label: string;
  /** One-line plain-language description of the module. */
  description: string;
  /** Heroicon rendered in the card header. */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Tailwind accent classes for the card's icon chip. */
  accentClass: string;
  /** Primary "Enter module" destination. */
  enterTo: string;
  /** Quick link to the module's dashboard. */
  dashboardTo: string;
  /** Quick link to the module's analytics. */
  analyticsTo: string;
  /** Which best-effort indicator loader feeds this card's headline counts. */
  indicatorSource: HomeIndicatorSource;
  /**
   * Short, plain-language summary of what a user can do in this module. Shown in
   * the "What you can do here" capability section.
   */
  capabilitySummary: string;
}

/**
 * Single source of truth for the modules surfaced on the global home dashboard.
 * Visibility is resolved per-user by `useAccessibleModules`, mirroring the
 * Sidebar's access rules (feature flags + LEGAL_ADMIN restriction + SUPER_ADMIN
 * override + module access). Adding a module is a config edit here.
 */
export const HOME_MODULES: HomeModule[] = [
  {
    id: 'security',
    sectionId: 'security',
    moduleId: 'security',
    label: 'Corporate Security',
    description:
      'Manage incidents, executive protection, threat intelligence, and investigations from one operational hub.',
    icon: ShieldExclamationIcon,
    accentClass: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    enterTo: SECURITY_DASHBOARD_PATH,
    dashboardTo: SECURITY_DASHBOARD_PATH,
    analyticsTo: SECURITY_ANALYTICS_PATH,
    indicatorSource: 'security',
    capabilitySummary:
      'Track open incidents, monitor threats, run investigations, and oversee executive protection.',
  },
  {
    id: 'legal',
    sectionId: 'legal',
    moduleId: 'legal',
    label: 'Legal & Compliance',
    description:
      'Oversee vendors, contracts, legal matters, governance, and regulatory compliance in one place.',
    icon: ScaleIcon,
    accentClass: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    enterTo: '/legal/dashboard',
    dashboardTo: '/legal/dashboard',
    analyticsTo: '/legal/analytics',
    indicatorSource: 'legal',
    capabilitySummary:
      'Manage vendors and contracts, handle legal matters, and track compliance obligations.',
  },
];
